import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  Paper,
  TablePagination,
  FormControlLabel,
  TextField,
  Divider,
  InputAdornment,
  Switch,
  Tooltip,
  Snackbar,
} from "@mui/material";
import { ReactComponent as SearchIcon } from "svgs/light/search-icon.svg";
import useStyles from "./styles";
import { KeyboardArrowDown } from "@material-ui/icons";
import { useEffect, useState, useMemo } from "react";
import { CustomDatePicker } from "components/molecules/CustomDatePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import CustomTable from "components/molecules/CustomTable";
import { ReactComponent as TickIconFill } from "svgs/light/tick-active.svg";
import { useSelector } from "react-redux";
import { userSelector } from "selectors/auth.selector";
import crowdSourceService from "services/crowdService";
import httpRequest from "services/httpRequest";
import { DOWNLOAD_PAYMENTS_LIST } from "services/apiLists";
import { Buffer } from "buffer";
import { Typography } from "@material-ui/core";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import CurationListModal from "./CurationListModal";
import InitiatePaymentModal from "./InitiatePaymentModal";
import { formatIndianCurrency } from "helpers/formateCurrency";
import toast from "react-hot-toast";
import crowdService from "services/crowdService";
import CopiedText from "components/molecules/CopiedText";
import { setFilter } from "actions/filter.actions";
import { useDispatch } from "react-redux";
import { filtersSelector } from "selectors/employee.selector";
import { AnyARecord } from "dns";
import DownloadFileModal from "./DownloadFileModal";

dayjs.extend(utc);

interface ICrowdDailyStat {
  date: string;
  earnings: number;
  data_points: number;
  transaction_status: string;
  job_id: any;
}

interface ICrowdSourceList {
  account_name: string;
  account_number: string;
  bank_name: string;
  compensation_amount: number;
  created_at: string;
  data_points_curated: number;
  deleted_at: any;
  employee_id: string;
  employee_name: string;
  entity_id: string;
  id: number;
  ifsc_code: string;
  reviewed: boolean;
  reviewed_by: number;
  status?: string;
  transaction_id: string;
  transaction_status: string;
  updated_at: string;
  work_date: string;
  total_earnings: number;
  amount_paid: number;
  amount_pending: number;
  total_data_points: number;
  daily_stats: ICrowdDailyStat[];
}

const TRANSACTION_STATUS = {
  paid: "success",
  pending: "pending",
  failed: "failed",
  inProgress: "in_progress",
  none: "N/A",
};

export const STATUS_OPTIONS = [
  {
    label: "All Status",
    value: "all",
  },
  {
    label: "Paid",
    value: TRANSACTION_STATUS.paid,
  },
  {
    label: "Pending",
    value: TRANSACTION_STATUS.pending,
  },
  {
    label: "Failed",
    value: TRANSACTION_STATUS.failed,
  },
  {
    label: "In Progress",
    value: TRANSACTION_STATUS.inProgress,
  },
  {
    label: "N/A",
    value: TRANSACTION_STATUS.none,
  },
];

const CrowdSourcePayment = ({
  readAccess,
  writeAccess,
}: {
  readAccess: boolean;
  writeAccess: boolean;
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const userinfo = useSelector(userSelector);
  const entityid = userinfo?.currentuserinfo?.entity_id?.id;
  const email = userinfo.currentuserinfo.id;
  const uniqueTypes: any[] = STATUS_OPTIONS;

  const filters = useSelector(filtersSelector);
  const paymentsFilters: any = filters?.naturalAnchorsPayment?.paymentsFilters;
  const { searchQuery, startDate, endDate, selectedType, page, limit } =
    paymentsFilters;

  const [reload, setReload] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<ICrowdSourceList[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isBatchPyaments, setIsBatchPayments] = useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<any>(null);
  const [analyticData, setAnalyticData] = useState<any>(null);
  const [excludedUsersList, setExcludedUsersList] = useState<any[]>([]);

  const [openDownloadFileModal, setOpenDownloadFileModal] =
    useState<boolean>(false);

  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery || "");

  const [globalSelectEnabled, setGlobalSelectEnabled] =
    useState<boolean>(false);

  useEffect(() => {
    const fetchOnReloadKeyChange = async () => {
      await getMultiplePayoutStatus();
      loadEarnings();
    };

    fetchOnReloadKeyChange();
  }, [reload, paymentsFilters]);

  useEffect(() => {
    const handler = setTimeout(() => {
      updateFilters({ searchQuery: debouncedSearch });
    }, 500); // ⏱ debounce delay (adjust if needed)

    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearch]);

  const reloadData = () => setReload(!reload);

  const updateFilters = (data: any) => {
    const updateData = {
      ...paymentsFilters,
      ...data,
    };
    dispatch(
      setFilter("naturalAnchorsPayment", "paymentsFilters", updateData || ""),
    );
  };

  const getDates = () => {
    const start_date = dayjs(startDate).startOf("day").utc().format();
    const end_date = dayjs(endDate).endOf("day").utc().format();

    return {
      start_date,
      end_date,
    };
  };

  const getStatus = (employee: any) => {
    let status = "pending";

    if (employee?.amount_to_be_paid < 0) {
      return TRANSACTION_STATUS.none;
    }

    if (employee?.amount_to_be_paid > 0) {
      status = TRANSACTION_STATUS.pending;
    } else if (employee?.amount_to_be_paid <= 0) {
      status = TRANSACTION_STATUS.paid;
    }

    return status;
  };

  const getMultiplePayoutStatus = async () => {
    await crowdSourceService.fetch_multiple_payout_status(
      entityid || "",
      getDates()?.start_date,
      getDates()?.end_date,
    );
  };

  const loadEarnings = () => {
    const formData = new FormData();
    formData.append("entity_id", entityid!);
    if (startDate || endDate) {
      formData.append("start_date", getDates()?.start_date);
      formData.append("end_date", getDates()?.end_date);
    }

    formData.append("page", (page + 1).toString());
    formData.append("limit", limit.toString());

    formData.append("search_string", searchQuery || "");
    if (
      selectedType &&
      selectedType?.value !== "all" &&
      selectedType?.value !== ""
    ) {
      formData.append("filter", selectedType?.value || "");
    }

    crowdSourceService
      .fetchEarnings(formData)
      .then((res: any) => {
        const udpatedList = res?.data?.data?.map((each: any, index: number) => {
          return {
            ...each,
            id: each?.employee_id,
          };
        });
        setUsers(udpatedList);

        // setPage(Math.max(0, (res?.data?.page ?? 1) - 1));
        setTotalPages(res.data?.total_pages);
        setTotalCount(res?.data?.total_count);
        // setLimit(res.data?.limit);

        setAnalyticData(res.data);

        if (globalSelectEnabled) {
          const uniqueIDs = [
            ...new Set([
              ...selectedRows?.map((each: any) => each.employee_id),
              ...udpatedList.map((each: any) => each.employee_id),
            ]),
          ];

          const excludeIds = [
            ...new Set([
              ...excludedUsersList?.map((each: any) => each.employee_id),
            ]),
          ];
          const excludedUniqueIds = uniqueIDs.filter(
            (id: any) => !excludeIds.includes(id),
          );
          const mappedData = [...selectedRows, ...udpatedList]?.filter(
            (each: any) => excludedUniqueIds.includes(each.employee_id),
          );

          setSelectedRows([...mappedData]);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    updateFilters({ page: newPage });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    updateFilters({ limit: +event.target.value, page: 0 });
  };

  const handleRowSelect = (id: any) => {
    const selectedIndex = selectedRows.indexOf(id);
    let newSelected: any = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1),
      );
    }

    setSelectedRows(newSelected);
  };

  const handleExcludeRows = (id: any) => {
    const selectedIndex = excludedUsersList.indexOf(id);
    let newSelected: any = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(excludedUsersList, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(excludedUsersList.slice(1));
    } else if (selectedIndex === excludedUsersList.length - 1) {
      newSelected = newSelected.concat(excludedUsersList.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        excludedUsersList.slice(0, selectedIndex),
        excludedUsersList.slice(selectedIndex + 1),
      );
    }

    setExcludedUsersList(newSelected);
  };

  const isSelected = (id: any) => {
    const selected = selectedRows.some((row) => row.id === id);
    return selected;
  };

  const handleDownload = async () => {
    const { start_date, end_date } = getDates();
    await crowdService.downloadPaymentList(entityid!, start_date, end_date);
  };

  const renderStatus = (employee: any) => {
    let status = "pending";

    if (
      employee?.total_amount === 0 &&
      employee?.amount_to_be_paid === 0 &&
      employee?.previous_pending_payments === 0
    ) {
      return null;
    }

    if (employee?.amount_to_be_paid < 0) {
      status = TRANSACTION_STATUS.none;
    } else if (employee?.amount_to_be_paid === 0) {
      status = TRANSACTION_STATUS.paid;
    } else if (employee?.amount_to_be_paid > 0) {
      status = TRANSACTION_STATUS.pending;
    }

    if (!status) return;

    let chipStyle = {};
    let statusName = status;
    if (status.toLowerCase() === TRANSACTION_STATUS.paid.toLowerCase()) {
      chipStyle = {
        background: "#C8E6C9", // green 100
        color: "#2E7D32", // green 800
        borderColor: "#2E7D32",
        border: "2px solid #2E7D32",
      };
    } else if (
      status.toLowerCase() === TRANSACTION_STATUS.pending.toLowerCase()
    ) {
      chipStyle = {
        background: "#FFE0B2", // orange 100
        color: "#EF6C00", // orange 800
        borderColor: "#EF6C00",
        border: "2px solid #EF6C00",
      };
    } else if (
      status.toLowerCase() === TRANSACTION_STATUS.failed.toLowerCase()
    ) {
      chipStyle = {
        background: "#FFCDD2", // red 100
        color: "#D32F2F", // red 700
        borderColor: "#D32F2F",
        border: "2px solid #D32F2F",
      };
    }

    if (!analyticData?.is_payment_acceptable) {
      if (status.toLowerCase() === TRANSACTION_STATUS.paid.toLowerCase()) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Chip
              label={`${
                statusName.charAt(0).toUpperCase() + statusName.slice(1)
              }`}
              variant="outlined"
              sx={{
                height: "24px",
                padding: "2px 8px",
                fontSize: "11px",
                color: "black",
                cursor: "pointer",
                "& .MuiChip-label": {
                  display: "block",
                  whiteSpace: "normal",
                  fontWeight: 300,
                },
                ...chipStyle,
              }}
            />
          </div>
        );
      }
      return "-";
    }

    const renderBtn = () => {
      if (status.toLowerCase() === TRANSACTION_STATUS.failed.toLowerCase()) {
        return (
          <Button
            disableFocusRipple
            disableRipple
            variant="outlined"
            className={classes.buttonSecondaryStyleSmall}
            style={{
              border: 0,
              textDecoration: "underline",
              textUnderlineOffset: 5,
              color: "#B71C1C",
              borderColor: "#B71C1C",
              borderWidth: 2,
            }}
            onClick={() => {
              setIsBatchPayments(false);
              setSelectedTransaction(employee);
              setOpenModal(true);
            }}
          >
            <RefreshRoundedIcon
              style={{
                height: 18,
                width: 18,
                marginRight: "3px",
              }}
            />
            Retry
          </Button>
        );
      }

      if (status.toLowerCase() === TRANSACTION_STATUS.paid.toLowerCase()) {
        return null;
      }

      if (
        status.toLowerCase() === TRANSACTION_STATUS.pending.toLowerCase() &&
        analyticData?.is_payment_acceptable
      ) {
        return (
          <Button
            disableFocusRipple
            disableRipple
            variant="outlined"
            className={classes.buttonSecondaryStyleSmall}
            disabled={globalSelectEnabled}
            style={{
              color: "#E65100",
              borderColor: "#E65100",
              borderWidth: 2,
              opacity: globalSelectEnabled ? 0.5 : 1,
            }}
            onClick={() => {
              if (
                status.toLowerCase() === TRANSACTION_STATUS.paid.toLowerCase()
              ) {
                setSelectedData(employee);
              } else if (
                status.toLowerCase() ===
                TRANSACTION_STATUS.pending.toLowerCase()
              ) {
                setIsBatchPayments(false);
                setSelectedTransaction(employee);
                setOpenModal(true);
              }
            }}
          >
            Make Payment
          </Button>
        );
      }

      return null;
    };

    if (status.toLowerCase() === TRANSACTION_STATUS.inProgress.toLowerCase())
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            style={{
              color: "#7152F3",
              fontWeight: 700,
              fontSize: "11px",
            }}
            variant="body2"
          >
            In Progress
          </Typography>
        </div>
      );

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Chip
          label={`${statusName.charAt(0).toUpperCase() + statusName.slice(1)}`}
          variant="outlined"
          sx={{
            height: "24px",
            padding: "2px 8px",
            fontSize: "11px",
            color: "black",
            cursor: "pointer",
            "& .MuiChip-label": {
              display: "block",
              whiteSpace: "normal",
              fontWeight: 300,
            },
            ...chipStyle,
          }}
        />

        {writeAccess && renderBtn()}
      </div>
    );
  };

  const renderAction = (employee: any) => {
    let status = "pending";

    if (employee?.amount_to_be_paid <= 0) {
      status = TRANSACTION_STATUS.paid;
    } else if (employee?.amount_to_be_paid > 0) {
      status = TRANSACTION_STATUS.pending;
    }

    if (!status) return;

    const renderBtn = () => {
      if (status.toLowerCase() === TRANSACTION_STATUS.failed.toLowerCase()) {
        return (
          <Button
            disableFocusRipple
            disableRipple
            variant="outlined"
            className={classes.buttonSecondaryStyleSmall}
            style={{
              border: 0,
              textDecoration: "underline",
              textUnderlineOffset: 5,
            }}
            onClick={() => {
              setIsBatchPayments(false);
              setSelectedTransaction(employee);
              setOpenModal(true);
            }}
          >
            <RefreshRoundedIcon
              style={{
                height: 18,
                width: 18,
                marginRight: "3px",
              }}
            />
            Retry
          </Button>
        );
      }

      return (
        <Button
          disableFocusRipple
          disableRipple
          variant="outlined"
          className={classes.buttonSecondaryStyleSmall}
          onClick={() => {
            setSelectedData(employee);
          }}
        >
          View Details
        </Button>
      );
    };

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {renderBtn()}
      </div>
    );
  };

  // Helper: format a simple date range
  const formatDateRange = (start: string, end: string) => {
    const s = dayjs.utc(start).local();
    const e = dayjs.utc(end).local();
    return `${s.format("DD MMM YYYY")} – ${e.format("DD MMM YYYY")}`;
  };

  const rendrPendingTdsAmount = (
    amount: any,
    ranges?: Array<{
      start_date: string;
      end_date: string;
      amount?: number;
    }> | null,
  ) => {
    if (!amount) return "-";

    const text = (
      <p
        style={{
          color: "#FF5252",
          fontWeight: 700,
          fontSize: "11px",
          textDecoration: "underline",
          cursor: "help",
          display: "inline",
          margin: 0,
        }}
      >
        Rs. {formatIndianCurrency(amount)}
      </p>
    );

    if (!ranges || ranges.length === 0) {
      return text;
    }

    return (
      <Tooltip
        arrow
        placement="top"
        title={
          <Box>
            {ranges.map((r, idx) => (
              <Typography
                key={`tds-range-${idx}`}
                variant="caption"
                style={{ display: "block" }}
              >
                {formatDateRange(r.start_date, r.end_date)} -
                <span
                  style={{
                    // color: "#FF5252",
                    fontWeight: 500,
                    fontSize: "11px",
                    // textDecoration: "underline",
                    cursor: "help",
                    display: "inline",
                    margin: 0,
                  }}
                >
                  {" "}
                  (Rs. {formatIndianCurrency(amount)})
                </span>
              </Typography>
            ))}
          </Box>
        }
      >
        {text}
      </Tooltip>
    );
  };

  const showUnPaidPendingTdsAmount = useMemo(() => {
    return users?.some(
      (each: any) => each?.pending_tds_amount?.total_amount > 0,
    );
  }, [users]);

  const tableHeadersData = [
    {
      content: (
        <FormControlLabel
          control={
            <Switch
              disabled={!analyticData?.is_payment_acceptable}
              checked={globalSelectEnabled}
              onChange={() => {
                const prvState = !globalSelectEnabled;
                setGlobalSelectEnabled(prvState);

                const unPaidUsers = users?.filter((employee: any) =>
                  isSelectableStatus(employee),
                );

                setSelectedRows(prvState ? unPaidUsers : []);
                setExcludedUsersList([]);
              }}
              color="secondary"
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#5ff352ff",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#5ff352ff",
                  opacity: 0.5,
                },
                "& .MuiSwitch-track": {
                  opacity: 0.3,
                },
              }}
            />
          }
          // label="Select All Names"
          label={
            <div>
              <Typography
                style={{
                  fontSize: "12px",
                  lineHeight: "10px !important",
                }}
                variant="body2"
              >
                Select All Names
              </Typography>
            </div>
          }
          sx={{
            "& .MuiFormControlLabel-label": {
              fontFamily: "Lexend",
              fontSize: "12px",
              fontWeight: 400,
            },
          }}
        />
      ),
      sx: {
        textAlign: "left",
        paddingLeft: "24px !important",
      },
    },
    {
      content: "Curated Points",
      sx: { textAlign: "center" },
    },
    {
      content: "Total Amount",
      sx: { textAlign: "center" },
    },
    {
      content: "TDS",
      sx: { textAlign: "center" },
    },

    ...(showUnPaidPendingTdsAmount
      ? [
          {
            content: "Pending TDS",
            sx: { textAlign: "center" },
          },
        ]
      : []),

    {
      content: "Paid",
      sx: { textAlign: "center" },
    },
    {
      content: "Unsettled Amount",
      sx: { textAlign: "center" },
    },
    {
      content: "Amount to be paid",
      sx: { textAlign: "center" },
    },

    {
      content: "Status",
      sx: { textAlign: "left" },
    },
    {
      content: "Details",
      sx: { textAlign: "left" },
    },
  ];

  const isSelectableStatus = (employee: any) => {
    const lowerStatus = getStatus(employee).toLowerCase();

    return (
      lowerStatus === TRANSACTION_STATUS.pending.toLowerCase() ||
      lowerStatus === TRANSACTION_STATUS.failed.toLowerCase()
    );
  };

  const tableBodyData = users?.map((employee: any) => [
    {
      content: (
        <FormControlLabel
          style={{
            paddingTop: "8px",
            paddingBottom: "8px",
          }}
          control={
            <Checkbox
              disabled={!analyticData?.is_payment_acceptable}
              color="secondary"
              checked={isSelected(employee.id)}
              onChange={(e) => {
                // const checked = e.target.checked;
                if (globalSelectEnabled) {
                  handleExcludeRows(employee);
                }

                if (isSelectableStatus(employee)) {
                  handleRowSelect(employee);
                }
              }}
              sx={{
                opacity: isSelectableStatus(employee) ? 1 : 0,
                color: "#7152F3",
                "&.Mui-checked": {
                  color: "#7152F3 !important",
                },
                "&.MuiCheckbox-indeterminate": {
                  color: "red",
                },
              }}
            />
          }
          label={
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="body2"
                style={{ fontWeight: 400, fontSize: "13px" }}
              >
                {employee?.name || "-"}
              </Typography>
              <CopiedText text={employee?.employee_id || "-"} />
            </div>
          }
          sx={{
            // padding: "5px 0px",
            "& .MuiFormControlLabel-label": {
              fontSize: "14px",
              fontWeight: 400,
              textAlign: "left",
            },
          }}
        />
      ),
      sx: {
        display: "flex",
        marginLeft: "19px",
      },
    },

    {
      content: (
        <Box>
          <p
            // variant="body2"
            style={{
              color: employee?.curated_points < 0 ? "orange" : "black",
              fontWeight: "400 !important",
              fontSize: "10px !important",
              padding: 0,
              margin: 0,
            }}
          >
            {employee?.curated_points !== 0
              ? `${formatIndianCurrency(employee?.curated_points)}`
              : "-"}
          </p>
          {employee?.previous_curated_points > 0 && (
            <Chip
              label={
                <p style={{ fontSize: "8px", padding: "4px" }}>
                  Previous Curated :
                  <span
                    style={{
                      fontWeight: 600,
                      color:
                        employee?.previous_curated_points < 0
                          ? "#FF5252"
                          : "black",
                    }}
                  >
                    {" "}
                    {formatIndianCurrency(employee?.previous_curated_points)}
                  </span>
                </p>
              }
              size="small"
              sx={{
                // my: 0.75,
                backgroundColor: "rgba(255,165,0,0.12)",
                padding: "0px",
                // color: "#7A4A00",
                // backgroundColor: "rgba(255,165,0,0.12)",
                // border: "1px solid rgba(255,165,0,0.3)",
                "& .MuiChip-label": { fontSize: "9px", padding: "0px 2px" },
              }}
            />
          )}
        </Box>
      ),

      sx: { textAlign: "center" },
    },

    {
      content: (
        <Box>
          <Typography
            variant="body2"
            style={{
              color: employee?.total_amount < 0 ? "orange" : "black",
            }}
          >
            {employee?.total_amount !== 0
              ? `Rs.${formatIndianCurrency(employee?.total_amount)}`
              : "-"}
          </Typography>
          {employee?.previous_pending_payments > 0 && (
            <Chip
              label={
                <p style={{ fontSize: "8px", padding: "4px" }}>
                  Previous pending :
                  <span
                    style={{
                      fontWeight: 600,
                      color:
                        employee?.previous_pending_payments < 0
                          ? "#FF5252"
                          : "black",
                    }}
                  >
                    {" "}
                    Rs.{" "}
                    {formatIndianCurrency(employee?.previous_pending_payments)}
                  </span>
                </p>
              }
              // label={`Prevous pending payment: Rs.${formatIndianCurrency(
              //   employee?.previous_pending_payments,
              // )}`}
              size="small"
              sx={{
                // my: 0.75,
                backgroundColor: "rgba(255,165,0,0.12)",
                padding: "0px",
                // color: "#7A4A00",
                // backgroundColor: "rgba(255,165,0,0.12)",
                // border: "1px solid rgba(255,165,0,0.3)",
                "& .MuiChip-label": { fontSize: "9px", padding: "0px 2px" },
              }}
            />
          )}
        </Box>
      ),
      sx: { textAlign: "center", py: "20px" },
    },
    {
      content: (
        <Box>
          <p
            // variant="body2"
            style={{
              color: employee?.tds_amount < 0 ? "orange" : "black",
              fontWeight: "400 !important",
              fontSize: "10px !important",
              padding: 0,
              margin: 0,
            }}
          >
            {employee?.tds_amount !== 0
              ? `Rs.${formatIndianCurrency(employee?.tds_amount)}`
              : "-"}
          </p>
          {employee?.previous_pending_tds > 0 && (
            <Chip
              label={
                <p style={{ fontSize: "8px", padding: "4px" }}>
                  Previous TDS :
                  <span
                    style={{
                      fontWeight: 600,
                      color:
                        employee?.previous_pending_tds < 0
                          ? "#FF5252"
                          : "black",
                    }}
                  >
                    {" "}
                    Rs. {formatIndianCurrency(employee?.previous_pending_tds)}
                  </span>
                </p>
              }
              size="small"
              sx={{
                // my: 0.75,
                backgroundColor: "rgba(255,165,0,0.12)",
                padding: "0px",
                // color: "#7A4A00",
                // backgroundColor: "rgba(255,165,0,0.12)",
                // border: "1px solid rgba(255,165,0,0.3)",
                "& .MuiChip-label": { fontSize: "9px", padding: "0px 2px" },
              }}
            />
          )}
        </Box>
      ),

      sx: { textAlign: "center" },
    },

    ...(showUnPaidPendingTdsAmount
      ? [
          {
            content: rendrPendingTdsAmount(
              employee?.pending_tds_amount?.total_amount,
              employee?.pending_tds_amount?.start_date_end_date,
            ),
            sx: { textAlign: "center" },
          },
        ]
      : []),

    {
      content: `Rs.${formatIndianCurrency(employee?.amount_paid)}`,
      sx: { textAlign: "center" },
    },
    {
      content: (
        <Typography
          variant="body2"
          style={{ color: employee?.unsettled_amount > 0 ? "red" : "black" }}
        >
          {employee?.unsettled_amount > 0
            ? `Rs.${formatIndianCurrency(employee?.unsettled_amount)}`
            : "-"}
        </Typography>
      ),
      sx: { textAlign: "center" },
    },

    {
      content: (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="body2"
            style={{
              color: employee?.amount_to_be_paid < 0 ? "orange" : "black",
            }}
          >
            {employee?.amount_to_be_paid !== 0
              ? `Rs.${formatIndianCurrency(employee?.amount_to_be_paid)}`
              : "-"}
          </Typography>

          {employee?.pending_withdrawal_request_amount > 0 && (
            <Chip
              label={`Pending: Rs.${formatIndianCurrency(
                employee?.pending_withdrawal_request_amount,
              )}`}
              size="small"
              sx={{
                ml: 0.75,
                color: "#7A4A00",
                backgroundColor: "rgba(255,165,0,0.12)",
                border: "1px solid rgba(255,165,0,0.3)",
                "& .MuiChip-label": { fontSize: "10px", padding: "0 6px" },
              }}
            />
          )}
        </Box>
      ),
      sx: { textAlign: "center" },
    },
    {
      content: renderStatus(employee),
      sx: {
        textAlign: "left",
      },
    },
    {
      content: renderAction(employee),
      sx: {
        textAlign: "left",
      },
    },
  ]);

  const cardsData = [
    {
      label: "Total curated points",
      value: analyticData?.total_curated_points || "0",
    },
    {
      label: "Total amount earned",
      // total_amount_to_be_paid
      value: analyticData?.total_amount_earned || "0",
    },
    {
      label: "Total TDS",
      value: analyticData?.total_tds_amount_paid || "0",
    },
    {
      label: "Total amount paid",
      value: analyticData?.total_amount_paid || "0",
    },

    {
      label: "Total unsettled amount",
      value: analyticData?.total_unsettled_amount || "0",
    },
    {
      label: "Total amount to be paid",
      // total_amount_paid
      value: analyticData?.total_amount_to_be_paid || "0",
    },
  ];

  return (
    <>
      <Grid container>
        <Grid item md={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <FormControl style={{ width: "150px", marginRight: "10px" }}>
              <Autocomplete
                classes={{ option: classes.option }}
                sx={{
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "#A2A1A81A",
                    borderRadius: "10px",
                    height: "55px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#A2A1A81A",
                    borderRadius: "10px",
                    height: "55px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #A2A1A81A",
                    borderRadius: "10px",
                    height: "55px",
                  },
                  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #A2A1A81A",
                    borderRadius: "10px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                    {
                      border: "1px solid #A2A1A81A",
                      borderRadius: "10px",
                      height: "55px",
                    },
                }}
                value={Array.from({ length: 10 }, (_, i) => {
                  const yr = dayjs().year() - i;
                  return { label: `${yr}`, value: yr };
                }).find((o) => o.value === (startDate || dayjs()).year())}
                popupIcon={<KeyboardArrowDown />}
                onChange={(event: any, newValue: any) => {
                  const selectedYear = newValue?.value ?? dayjs().year();
                  const currentMonthIdx = (startDate || dayjs()).month();
                  const maxMonthIdx =
                    selectedYear === dayjs().year() ? dayjs().month() : 11;
                  const nextMonthIdx = Math.min(currentMonthIdx, maxMonthIdx);
                  const newStart = dayjs()
                    .year(selectedYear)
                    .month(nextMonthIdx)
                    .startOf("month");
                  const newEnd = dayjs()
                    .year(selectedYear)
                    .month(nextMonthIdx)
                    .endOf("month");

                  updateFilters({ startDate: newStart, endDate: newEnd });
                  // setStartDate(newStart);
                  // setEndDate(newEnd);
                }}
                options={Array.from({ length: 10 }, (_, i) => {
                  const yr = dayjs().year() - i;
                  return { label: `${yr}`, value: yr };
                })}
                disableClearable
                getOptionLabel={(option: any) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Year"
                    style={{ minHeight: "45px" }}
                    InputProps={{
                      ...params.InputProps,
                      style: {
                        fontFamily: "Lexend",
                        fontSize: "14px",
                        fontWeight: 300,
                        border: "none",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            <FormControl style={{ width: "150px", marginRight: "10px" }}>
              <Autocomplete
                classes={{ option: classes.option }}
                sx={{
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "#A2A1A81A",
                    borderRadius: "10px",
                    height: "55px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#A2A1A81A",
                    borderRadius: "10px",
                    height: "55px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #A2A1A81A",
                    borderRadius: "10px",
                    height: "55px",
                  },
                  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #A2A1A81A",
                    borderRadius: "10px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                    {
                      border: "1px solid #A2A1A81A",
                      borderRadius: "10px",
                      height: "55px",
                    },
                }}
                value={Array.from(
                  {
                    length:
                      (startDate || dayjs()).year() === dayjs().year()
                        ? dayjs().month() + 1
                        : 12,
                  },
                  (_, m) => ({
                    label: dayjs().month(m).format("MMMM"),
                    value: m,
                  }),
                ).find((o) => o.value === (startDate || dayjs()).month())}
                popupIcon={<KeyboardArrowDown />}
                onChange={(event: any, newValue: any) => {
                  const selectedMonthIdx =
                    newValue?.value ?? (startDate || dayjs()).month();
                  const selectedYear = (startDate || dayjs()).year();
                  const maxMonthIdx =
                    selectedYear === dayjs().year() ? dayjs().month() : 11;
                  const nextMonthIdx = Math.min(selectedMonthIdx, maxMonthIdx);
                  const newStart = dayjs()
                    .year(selectedYear)
                    .month(nextMonthIdx)
                    .startOf("month");
                  const newEnd = dayjs()
                    .year(selectedYear)
                    .month(nextMonthIdx)
                    .endOf("month");
                  updateFilters({ startDate: newStart, endDate: newEnd });
                  // setStartDate(newStart);
                  // setEndDate(newEnd);
                }}
                options={Array.from(
                  {
                    length:
                      (startDate || dayjs()).year() === dayjs().year()
                        ? dayjs().month() + 1
                        : 12,
                  },
                  (_, m) => ({
                    label: dayjs().month(m).format("MMMM"),
                    value: m,
                  }),
                )}
                disableClearable
                getOptionLabel={(option: any) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Month"
                    style={{ minHeight: "45px" }}
                    InputProps={{
                      ...params.InputProps,
                      style: {
                        fontFamily: "Lexend",
                        fontSize: "14px",
                        fontWeight: 300,
                        border: "none",
                      },
                    }}
                  />
                )}
              />
            </FormControl>

            <TextField
              type="search"
              size="small"
              variant="outlined"
              placeholder="Search"
              value={debouncedSearch}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDebouncedSearch(e.target.value)
              }
              sx={{
                width: "80%",
                maxWidth: 400,
                marginLeft: "auto",
                marginRight: "10px",
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: "#A2A1A833",
                  borderRadius: "10px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      style={{ height: 18, width: 18, color: "#16151C" }}
                    />
                  </InputAdornment>
                ),
                style: {
                  fontFamily: "Lexend",
                  fontSize: 14,
                  fontWeight: 300,
                  padding: "5px 14px",
                },
              }}
            />

            {/* <FormControl style={{ width: "200px" }}>
              <Autocomplete
                classes={{
                  option: classes.option,
                }}
                sx={{
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "#A2A1A81A",
                    borderRadius: "10px",
                    height: "55px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#A2A1A81A",
                    borderRadius: "10px",
                    height: "55px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #A2A1A81A",
                    borderRadius: "10px",
                    height: "55px",
                  },
                  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #A2A1A81A",
                    borderRadius: "10px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                    {
                      border: "1px solid #A2A1A81A",
                      borderRadius: "10px",
                      height: "55px",
                    },
                }}
                value={selectedType || {}}
                popupIcon={<KeyboardArrowDown />}
                onChange={(event: any, newValue: any) =>
                  // setSelectedType(newValue)
                  updateFilters({ selectedType: newValue })
                }
                // getOptionLabel={(option: any) => option.value}
                options={uniqueTypes}
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select Type"
                    style={{
                      minHeight: "45px",
                    }}
                    InputProps={{
                      ...params.InputProps,
                      style: {
                        fontFamily: "Lexend",
                        fontSize: "16px",
                        fontWeight: 400,
                        border: "none",
                      },
                    }}
                  />
                )}
              />
            </FormControl> */}
            {writeAccess && analyticData?.is_payment_acceptable && (
              <Button
                onClick={() => {
                  setIsBatchPayments(true);
                  setOpenModal(true);
                }}
                disableFocusRipple
                disableRipple
                variant="outlined"
                className={classes.buttonSecondaryStyle}
                disabled={selectedRows?.length === 0}
                style={{
                  opacity: selectedRows?.length === 0 ? 0.5 : 1,
                }}
              >
                Make Payments
              </Button>
            )}


            <Button
              variant="contained"
              className={classes.buttonPrimaryStyle}
              onClick={() => setOpenDownloadFileModal(true)}
            >
              Donwload
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Divider />

      <div
        style={{
          display: "flex",
          gap: 10,
          width: "100%",
          margin: "5px auto",
          marginBottom: "15px",
        }}
      >
        {cardsData?.map((each: any, index: number) => {
          return (
            <div
              key={index}
              style={{
                backgroundColor: "white",
                padding: "8px",
                borderRadius: "10px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                boxShadow: "0px 4px 10px 0px #0000001A",
                flex: 1,
                border: "0.5px solid #7152F3",
              }}
            >
              <Typography variant="h5" style={{ color: "#16151C" }}>
                {each.value}
              </Typography>

              <Typography
                variant="body2"
                style={{
                  color: "#16151C",
                  paddingLeft: "5px",
                  fontSize: 12,
                  fontWeight: 400,
                }}
              >
                {each.label}
              </Typography>
            </div>
          );
        })}
      </div>

      {!analyticData?.is_payment_acceptable && users?.length && (
        <div
          style={{
            position: "relative",
            top: 0,
            backgroundColor: "white",
            height: 50,
          }}
        >
          <div className={classes.marqueeContainer}>
            <div className={classes.marqueeText}>
              {[1, 2, 3, 4, 5, 6, 7, 8]?.map((each: any, index: number) => {
                return (
                  <span
                    key={index}
                    style={{ marginRight: "50px", color: "#D32F2F" }}
                  >
                    Pending payments appear in the latest month if they are any
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <Paper sx={{ width: "100%", overflow: "hidden" }} className="mt-10">
        <CustomTable
          headersDataList={tableHeadersData}
          bodyDataList={tableBodyData}
          NoDataFoundText={"No Data Found"}
          loading={loading}
          showPagenation={false}
          tableSX={{ height: "calc(100vh - 250px)" }}
        />
        <div style={{ marginRight: "100px" }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </Paper>

      {openModal && (
        <InitiatePaymentModal
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            setSelectedTransaction(null);
          }}
          selectedTransaction={selectedTransaction}
          selectedRows={selectedRows?.filter((employee: any) =>
            isSelectableStatus(employee),
          )}
          start_date={getDates()?.start_date || ""}
          end_date={getDates()?.end_date || ""}
          setIsBatchPayments={setIsBatchPayments}
          isBatchPyaments={isBatchPyaments}
          reloadData={() => {
            setGlobalSelectEnabled(false);
            setSelectedRows([]);
            setExcludedUsersList([]);
            reloadData();
          }}
          selectAllForBatchPayments={globalSelectEnabled === true ? "all" : ""}
          deSelectedUsersList={excludedUsersList?.map(
            (each: any) => each.employee_id,
          )}
        />
      )}

      {selectedData && (
        <CurationListModal
          open={selectedData ? true : false}
          onClose={() => {
            setSelectedData(null);
          }}
          selectedData={selectedData}
          start_date={getDates()?.start_date || ""}
          end_date={getDates()?.end_date || ""}
        />
      )}

      {openDownloadFileModal && (
        <DownloadFileModal
          open={openDownloadFileModal}
          onClose={() => setOpenDownloadFileModal(false)}
          onDownload={handleDownload}
          entityid={entityid!}
          start_date={getDates()?.start_date || ""}
          end_date={getDates()?.end_date || ""}
        />
      )}
    </>
  );
};

export default CrowdSourcePayment;
